import { awl_callback_debounce } from '../utils/awl_callback_debounce';
import angular from 'angular';
import { A9Service } from '../system';
import { type IEventEmitter, Service } from '../system';
import { type IValueStorage } from '../system/services/storage';

let firefoxPopup = false;

export enum ZoomEventsEnum {
    Auto = 'auto',
    Value = 'value'
}

export const ZOOM_AUTO = 'zoom.auto';
export const ZOOM_VALUE = 'zoom.value';

export const ZOOM_MIN_VALUE = 30;
export const ZOOM_MAX_VALUE = 150;
export const ZOOM_DEFAULT_VALUE = 100;

export interface IZoomService extends IEventEmitter {
    isAuto: () => boolean;
    getValue: () => number;
    getMaxValue: () => number;
    getMinValue: () => number;
    setValue: (val: number) => void;
    setAuto: () => void;
}

@A9Service('zoomService')
class ZoomService extends Service implements IZoomService {
    private minValue: number;
    private maxValue: number;
    private value: number;
    private body: JQuery;
    private win: JQuery;
    private isIE: boolean;
    private isFirefox: boolean;
    private supportCss3: boolean;
    private zoom: number;
    private auto: boolean;
    private browserZoomLevel: number;

    constructor(
        private $window: JQuery,
        private $timeout: angular.ITimeoutService,
        private valueStorage: IValueStorage,
        private AutoHeight: any,
        private userSettings: any,
        private userAuth: any
    ) {
        super();
        this.minValue = ZOOM_MIN_VALUE;
        this.maxValue = ZOOM_MAX_VALUE;
        this.value = ZOOM_DEFAULT_VALUE;
        this.body = angular.element('body');
        this.win = angular.element($window);
        this.isIE = false;
        this.isFirefox = false;
        this.supportCss3 = this.supportFullCss3();
        this.zoom = 1;
        this.auto = true;
        this.browserZoomLevel = this.getZoomLevel();
        this.checkBrowser();
        this.init();
        this.initResizeHandler();
    }

    public isAuto(): boolean {
        return this.auto;
    }

    public getValue(): number {
        return this.value;
    }

    public getMaxValue(): number {
        return this.maxValue;
    }

    public getMinValue(): number {
        return this.minValue;
    }

    public setValue(val: number): void {
        this.valueStorage.set(ZOOM_VALUE, val);
        this.valueStorage.set(ZOOM_AUTO, false);
        this.userSettings.setConfig(ZOOM_VALUE, val);
        this.userSettings.setConfig(ZOOM_AUTO, 'false');

        this.auto = false;
        this.emit(ZoomEventsEnum.Auto, this.auto);
        this.update(val);
    }

    public setAuto(): void {
        this.valueStorage.set(ZOOM_VALUE, ZOOM_DEFAULT_VALUE);
        this.valueStorage.set(ZOOM_AUTO, true);
        this.userSettings.setConfig(ZOOM_VALUE, ZOOM_DEFAULT_VALUE);
        this.userSettings.setConfig(ZOOM_AUTO, 'true');

        this.auto = true;
        this.emit(ZoomEventsEnum.Auto, this.auto);
        this.autoUpdate();
    }

    private initResizeHandler(): void {
        const onResize = awl_callback_debounce(() => {
            if (this.auto) {
                this.autoUpdate();
            } else {
                const level = this.getZoomLevel();
                if (this.browserZoomLevel != level) {
                    this.browserZoomLevel = level;
                    this.update(this.browserZoomLevel);
                } else {
                    this.doFit();
                }
            }
            // 20170517 Tmax added to proper window resize on all zoom cases
            this.AutoHeight.initializeWindowSize();
        }, 100);
        this.win.on('resize', onResize);
    }

    private inRange(val: number): boolean {
        return val >= this.minValue && val <= this.maxValue;
    }

    private init(): void {
        if (this.valueStorage.get(ZOOM_AUTO) !== null) {
            if (this.valueStorage.get(ZOOM_AUTO) === false) {
                this.auto = false;
                if (this.valueStorage.get(ZOOM_VALUE)) {
                    const v = parseInt(this.valueStorage.get(ZOOM_VALUE));
                    this.update(this.inRange(v) ? v : ZOOM_DEFAULT_VALUE);
                } else {
                    this.userSettings.config(ZOOM_VALUE).then(value => {
                        const v = value || ZOOM_DEFAULT_VALUE;
                        this.valueStorage.set(ZOOM_VALUE, v);
                        this.update(this.inRange(v) ? v : ZOOM_DEFAULT_VALUE);
                    });
                }
            } else {
                this.autoUpdate();
            }
        } else {
            this.userSettings.config(ZOOM_VALUE).then(auto => {
                if (auto === 'true' || auto === 'false') {
                    this.valueStorage.set(ZOOM_VALUE, auto === 'true');
                    this.emit(ZoomEventsEnum.Auto, auto === 'true');
                } else {
                    this.userSettings.setConfig(ZOOM_AUTO, 'true');
                    this.valueStorage.set(ZOOM_AUTO, true);
                    this.emit(ZoomEventsEnum.Auto, true);
                }
                this.init();
            });
        }
        this.userAuth.on('logout', () => {
            this.valueStorage.remove(ZOOM_AUTO);
            this.valueStorage.remove(ZOOM_VALUE);
        });
    }

    private autoUpdate(): void {
        this.update(Math.min(Math.round((window.innerWidth * 100) / 1600), 100));
    }

    private doFit(): void {
        const w = this.win.width(),
            h = this.win.height(),
            ratio = 1 / this.zoom;
        this.body.css({ width: w * ratio, height: h * ratio });
        this.getMargins();
        this.body.css('transform-origin', '0 0');
    }

    private getMargins(): { marginLeft: number; marginTop: number } {
        let margins = {
            marginLeft: 0,
            marginTop: 0
        };
        if (this.isFirefox) {
            const w = this.win.width() ?? 0,
                h = this.win.height() ?? 0,
                ratio = 1 / this.zoom;
            margins = {
                marginLeft: (w - w * ratio) / 2,
                marginTop: (h - h * ratio) / 2
            };
            if (!firefoxPopup) {
                const timeout = this.$timeout;
                const zoom = this.zoom;
                this.body.on('DOMNodeInserted', '.popover', function () {
                    const el: HTMLElement = this as HTMLElement;
                    firefoxPopup = true;
                    timeout(function () {
                        const top = parseInt(el.style.top);
                        if (top) {
                            el.style.top = Math.round(top / zoom) + 'px';
                        }
                        const left = parseInt(el.style.left);
                        if (left) {
                            el.style.left = Math.round(left / zoom) + 'px';
                        }
                    });
                });
            }
        }
        return margins;
    }

    private update(val: number): void {
        val = parseInt(String(val)) || 0;
        if (this.inRange(val)) {
            this.value = val;
            this.zoom = this.value / 100;
            if (this.value == this.browserZoomLevel) {
                this.zoom = 1;
            } else {
                this.zoom = this.value / this.browserZoomLevel;
            }

            this.body.css({ transform: `scale(${this.zoom},${this.zoom})` });
            this.doFit();
            // 20170517 Tmax
            // 201707 AnatolyVasilev cancelled that was cancelled added that was added
            this.AutoHeight.initializeWindowSize();
            this.emit(ZoomEventsEnum.Value, this.value);
        }
    }

    private checkBrowser(): void {
        const userAgent = ('navigator' in window && 'userAgent' in navigator && navigator.userAgent.toLowerCase()) || '';
        // let vendor = 'navigator' in window && 'vendor' in navigator && navigator.vendor.toLowerCase() || '';
        this.isIE = /Trident\/7\./i.test(userAgent);
        this.isFirefox = /Firefox/i.test(userAgent);
    }

    private supportFullCss3(): boolean {
        const div = document.createElement('div');
        div.style.display = 'flex';
        const s1 = div.style.display == 'flex';
        const s2 = 'perspective' in div.style;
        return s1 && s2;
    }

    private getZoomLevel(): number {
        let screenPixelRatio = 0;
        if (window.devicePixelRatio && this.supportCss3) {
            screenPixelRatio = window.devicePixelRatio;
            // http://www.quirksmode.org/blog/archives/2012/07/more_about_devi.html
            // On iOS Retina devices, screen.width gives the width in dips. So both a retina and a non-retina iPad report 768 in portrait mode.
            // On the three Android devices, screen.width gives the width in physical pixels; 480, 720, and 800, respectively.

            // TMax devicePixelRatio cannot be used here because desktop/ios html running on device-independent pixels (tested on desktop chrome with DPR change 1-3)
            // TODO detect Android / Windows mobile browsers case and use ratio for zoom levels
            screenPixelRatio = 1;
        } else if (window.screenX == 0) {
            screenPixelRatio = (window.outerWidth - 8) / window.innerWidth;
        } else {
            const scr = window.frames.screen as any;
            screenPixelRatio = scr.deviceXDPI / scr.systemXDPI;
        }
        return Math.round(screenPixelRatio * 100);
    }
}
